<script>
import api from "@/helpers/api-rest/api.js";
import format from "date-fns/format";
import store from "@/state/store";
import { required, helpers } from "vuelidate/lib/validators";
import { errorMessages } from "@/helpers/validations/errorMessages";
import { notifyValidationFailedAndFocusFirstInvalid } from "@/helpers/validations/vuelidateFormFeedback";
import { mapState } from "vuex";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import RelationsConfigSection from "./partials/RelationsConfigSection.vue";
import ExternalizationsSection from "./partials/ExternalizationsSection.vue";
import PayerEntitySection from "./partials/PayerEntitySection.vue";
import PerceptorSection from "./partials/PerceptorSection.vue";
import ExpenseSection from "./partials/ExpenseSection.vue";
import PaymentSection from "./partials/PaymentSection.vue";
import AdditionalInfoSection from "./partials/AdditionalInfoSection.vue";
import OrderNumberSection from "./partials/OrderNumberSection.vue";
import ActionTypeSection from "./partials/ActionTypeSection.vue";

// Custom validators
const decimalValidator = helpers.withParams(
  { type: "decimal" },
  (value) => !value || (!Number.isNaN(value) && Number.parseFloat(value) >= 0)
);

export default {
  name: "ResumeInvoiceModal",
  components: {
    LoadingOverlay,
    RelationsConfigSection,
    ExternalizationsSection,
    PayerEntitySection,
    PerceptorSection,
    ExpenseSection,
    PaymentSection,
    AdditionalInfoSection,
    OrderNumberSection,
    ActionTypeSection,
  },
  props: {
    id: { type: String, default: "ModalResumeInvoice" },
    resume_id: {
      type: [Number, String],
      default: null,
    },
    essay_id: {
      type: [Number, String],
      required: true,
    },
    project_id: {
      type: [Number, String],
      default: null,
    },
    control_economic_id: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      loadingResume: false,
      loadingProject: false,
      essay: {},
      project: {},
      localResume: {},
      isFormativeAction: false,
      errorMessages,
      // Select options from project
      selectOptionsSubsidizableActuations: [],
      selectOptionsSubsidizableActions: [],
      selectOptionsCodings: [],
      selectOptionsPhases: [],
      selectOptionsPrimaryProjects: [],
      selectOptionsExpenseTypologies: [],
      // Justification type options
      justificationTypeOptions: [
        { value: "coste_real", text: "Coste Real" },
      ],
    };
  },
  computed: {
    ...mapState("loading", {
      globalLoading: (state) => state.loading,
    }),
    modalTitle() {
      return this.localResume?.id ? "Editar justificante" : "Añadir justificante";
    },
    vatVerificationStatus() {
      if (
        this.localResume.net_cost &&
        this.localResume.iva_cost &&
        this.localResume.total_cost
      ) {
        const calculatedTotal =
          Number.parseFloat(this.localResume.net_cost || 0) +
          Number.parseFloat(this.localResume.iva_cost || 0);
        const actualTotal = Number.parseFloat(this.localResume.total_cost || 0);
        return Math.abs(calculatedTotal - actualTotal) < 0.01;
      }
      return null;
    },
    saveSpinnerMessage() {
      if (this.loadingResume || this.loadingProject) {
        return "Cargando datos...";
      }
      if (this.globalLoading || this.loading) {
        return "Guardando...";
      }
      return "";
    },
  },
  watch: {
    "localResume.action_type": {
      handler(newValue) {
        // Check if it's a formative action
        this.isFormativeAction =
          newValue &&
          newValue.toLowerCase().includes("formativ");
      },
    },
    "localResume.net_cost": {
      handler() {
        this.syncDocumentTotalCost();
      },
    },
    "localResume.iva_cost": {
      handler() {
        this.syncDocumentTotalCost();
      },
    },
    "localResume.net_imputed_cost": {
      handler() {
        this.syncImputedTotalCost();
      },
    },
    "localResume.iva_imputed_cost": {
      handler() {
        this.syncImputedTotalCost();
      },
    },
  },
  methods: {
    initializeResume(resume) {
      // Map only the fields we need, excluding relations
      const resumeData = {
        id: resume.id || null,
        action_type: resume.action_type || null,
        dni: resume.dni || null,
        name: resume.name || null,
        material: resume.material || null,
        n_invoice: resume.n_invoice || null,
        invoice_date: resume.invoice_date || null,
        net_cost: resume.net_cost || null,
        iva_cost: resume.iva_cost || null,
        total_cost: resume.total_cost || null,
        net_imputed_cost: resume.net_imputed_cost || null,
        iva_imputed_cost: resume.iva_imputed_cost || null,
        total_imputed_cost: resume.total_imputed_cost || null,
        document_name: resume.document_name || null,
        document_path: resume.document_path || null,
        date: resume.date || null,
        payment_document_name: resume.payment_document_name || null,
        payment_document_path: resume.payment_document_path || null,
        observations: resume.observations || null,
        tracking_control_economic_id: resume.tracking_control_economic_id || null,
        subsidizable_actuation_id: resume.subsidizable_actuation_id || null,
        subsidizable_action_id: resume.subsidizable_action_id || null,
        coding_id: resume.coding_id || null,
        phase_id: resume.phase_id || null,
        primary_project_id: resume.primary_project_id || null,
        expense_typology_id: resume.expense_typology_id || null,
        contract_amount_excl_vat: resume.contract_amount_excl_vat || null,
        award_procedure: resume.award_procedure || null,
        procurement_file_number: resume.procurement_file_number || null,
        budget_chapter: resume.budget_chapter || null,
        payment_center_id: resume.payment_center_id || null,
        order_number: resume.order_number || null,
        vat_verification: !!resume.vat_verification,
        expense_outside_spain_in_eu: !!resume.expense_outside_spain_in_eu,
        expense_outside_eu: !!resume.expense_outside_eu,
        justification_type: resume.justification_type || "coste_real",
      };
      
      // Format dates
      if (resumeData.invoice_date) {
        resumeData.invoice_date = format(
          new Date(resumeData.invoice_date),
          "yyyy-MM-dd"
        );
      }
      if (resumeData.date) {
        resumeData.date = format(
          new Date(resumeData.date),
          "yyyy-MM-dd"
        );
      }
      
      // Use $set to ensure reactivity in Vue 2
      this.$set(this, "localResume", resumeData);
      this.checkFormativeAction();
      this.$nextTick(() => {
        this.syncDocumentTotalCost();
        this.syncImputedTotalCost();
      });
    },
    syncDocumentTotalCost() {
      if (!this.localResume || typeof this.localResume !== "object") {
        return;
      }
      const net = Number.parseFloat(this.localResume.net_cost);
      const iva = Number.parseFloat(this.localResume.iva_cost);
      const n = Number.isFinite(net) ? net : 0;
      const i = Number.isFinite(iva) ? iva : 0;
      const total = (n + i).toFixed(2);
      if (this.localResume.total_cost !== total) {
        this.$set(this.localResume, "total_cost", total);
      }
      this.updateVatVerification();
    },
    syncImputedTotalCost() {
      if (!this.localResume || typeof this.localResume !== "object") {
        return;
      }
      const net = Number.parseFloat(this.localResume.net_imputed_cost);
      const iva = Number.parseFloat(this.localResume.iva_imputed_cost);
      const n = Number.isFinite(net) ? net : 0;
      const i = Number.isFinite(iva) ? iva : 0;
      const total = (n + i).toFixed(2);
      if (this.localResume.total_imputed_cost !== total) {
        this.$set(this.localResume, "total_imputed_cost", total);
      }
    },
    checkFormativeAction() {
      // this.isFormativeAction =
      //   this.localResume.action_type &&
      //   this.localResume.action_type.toLowerCase().includes("formativ");
    },
    updateVatVerification() {
      if (this.vatVerificationStatus !== null) {
        this.$set(
          this.localResume,
          "vat_verification",
          this.vatVerificationStatus
        );
      }
    },
    async loadData() {
      // Load project data directly if project_id is provided
      if (this.project_id) {
        await this.loadProjectData(this.project_id);
      }
      // Load resume data when modal opens
      // This ensures data is always fresh when opening the modal
      if (this.resume_id) {
        await this.loadResumeData(this.resume_id);
      } else {
        this.initializeResume({});
      }
    },
    async loadResumeData(resumeId) {
      // Avoid duplicate loads if already loading
      if (this.loadingResume) {
        return;
      }
      this.loadingResume = true;
      try {
        const response = await api.get({
          url: `/api/v1/resume-invoices/${resumeId}`,
        });
        const resume = response?.data?.data;
        this.initializeResume(resume);
      } catch (error) {
        console.error("Error loading resume:", error);
        this.$bvToast.toast("Error al cargar los datos del justificante", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
        this.initializeResume({});
      } finally {
        this.loadingResume = false;
      }
    },
    async loadProjectData(projectId) {
      this.loadingProject = true;
      try {
        const response = await api.get({
          url: `/api/v1/projects/${projectId}`,
        });
        this.project = response?.data?.data;
        this.loadSelectOptions();
      } catch (error) {
        console.error("Error loading project:", error);
        this.$bvToast.toast("Error al cargar los datos del proyecto", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.loadingProject = false;
      }
    },
    loadSelectOptions() {
      // Load options from project relations
      this.selectOptionsSubsidizableActuations =
        this.project.subsidizable_actuations?.map((item) => ({
          value: item.id,
          text: item.name,
        })) || [];
      this.selectOptionsSubsidizableActions =
        this.project.subsidizable_actions?.map((item) => ({
          value: item.id,
          text: item.name,
        })) || [];
      this.selectOptionsCodings =
        this.project.codings?.map((item) => ({
          value: item.id,
          text: item.name,
        })) || [];
      this.selectOptionsPhases =
        this.project.phases?.map((item) => ({
          value: item.id,
          text: item.name,
        })) || [];
      this.selectOptionsPrimaryProjects =
        this.project.primary_projects?.map((item) => ({
          value: item.id,
          text: item.name,
        })) || [];
      this.selectOptionsExpenseTypologies =
        this.project.expense_typologies?.map((item) => ({
          value: item.id,
          text: item.name,
        })) || [];
    },
    showElement(roleId = [], show = true) {
      const role = store.getters["auth/role"];
      return roleId.includes(role.id) && show;
    },
    formatDate(d = new Date()) {
      return `${String(d.getDate()).padStart(2, "0")}${String(
        d.getMonth() + 1
      ).padStart(2, "0")}${d.getFullYear()}${String(d.getHours()).padStart(
        2,
        "0"
      )}${String(d.getMinutes()).padStart(2, "0")}`;
    },
    onFileChangeResume(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.$set(
        this.localResume,
        "payment_document_name",
        this.formatDate() + "-" + file.name
      );
      this.uploadFile(file, "resumes")
        .then((response) => {
          const fileData = response?.data?.data;
          this.$set(this.localResume, "payment_document_path", fileData.path);
        })
        .catch((error) => {
          console.error("Error al cargar el archivo:", error);
          this.$set(this.localResume, "payment_document_name", null);
          this.$set(this.localResume, "payment_document_path", null);
        });
    },
    uploadFile(file, folder) {
      const formData = new FormData();
      formData.set("file", file);
      formData.append("path", folder);
      const config = { headers: { "content-type": "multipart/form-data" } };
      const apiEndpoint = `/api/v1/upload-file`;
      return api.post({ url: apiEndpoint, data: formData, config });
    },
    prepareResumeData() {
      this.syncDocumentTotalCost();
      this.syncImputedTotalCost();
      const data = { ...this.localResume };
      // Ensure tracking_control_economic_id is set
      if (this.control_economic_id) {
        data.tracking_control_economic_id = this.control_economic_id;
      }
      // Ensure essay_id is set
      if (this.essay_id) {
        data.essay_id = this.essay_id;
      }
      return data;
    },
    createResume() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        notifyValidationFailedAndFocusFirstInvalid(this);
        return;
      }
      this.loading = true;
      const data = this.prepareResumeData();
      const apiEndpoint = `/api/v1/resume-invoices`;
      api
        .post({ url: apiEndpoint, data: data })
        .then((response) => {
          this.loading = false;
          const newResume = response?.data?.data;
          this.$emit("success", newResume);
          this.$bvModal.hide(this.id);
          this.resetForm();
        })
        .catch((error) => {
          this.loading = false;
          console.error("Error al crear el justificante:", error);
          this.$bvToast.toast(
            error?.response?.data?.message || "Error al crear el justificante",
            {
              title: "Error",
              variant: "danger",
              solid: true,
            }
          );
        });
    },
    updateResume() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        notifyValidationFailedAndFocusFirstInvalid(this);
        return;
      }
      this.loading = true;
      const data = this.prepareResumeData();
      const apiEndpoint = `/api/v1/resume-invoices`;
      api
        .put({ url: apiEndpoint, data: data, id: this.localResume.id })
        .then((response) => {
          this.loading = false;
          const updatedResume = response?.data?.data;
          this.$emit("success", updatedResume);
          this.$bvModal.hide(this.id);
          this.resetForm();
        })
        .catch((error) => {
          this.loading = false;
          console.error("Error al editar el justificante:", error);
          this.$bvToast.toast(
            error?.response?.data?.message || "Error al editar el justificante",
            {
              title: "Error",
              variant: "danger",
              solid: true,
            }
          );
        });
    },
    submitFormResume() {
      if (this.localResume?.id) {
        return this.updateResume();
      }
      return this.createResume();
    },
    cancelForm() {
      this.$bvModal.hide(this.id);
      this.resetForm();
    },
    resetForm() {
      this.localResume = {};
      this.isFormativeAction = false;
      this.essay = {};
      this.project = {};
      this.selectOptionsSubsidizableActuations = [];
      this.selectOptionsSubsidizableActions = [];
      this.selectOptionsCodings = [];
      this.selectOptionsPhases = [];
      this.selectOptionsPrimaryProjects = [];
      this.selectOptionsExpenseTypologies = [];
      this.$v.$reset();
    },
    // Update methods for two-way binding
    updateField(field, value) {
      this.$set(this.localResume, field, value);
    },
  },
  validations: {
    localResume: {
      justification_type: { required },
      payment_center_id: { required },
      // Decimal validations
      contract_amount_excl_vat: { decimal: decimalValidator },
      net_cost: { decimal: decimalValidator },
      iva_cost: { decimal: decimalValidator },
      total_cost: { decimal: decimalValidator },
      net_imputed_cost: { decimal: decimalValidator },
      iva_imputed_cost: { decimal: decimalValidator },
      total_imputed_cost: { decimal: decimalValidator },
    },
  },
};
</script>

<template>
  <b-modal
    :id="id"
    :title="modalTitle"
    @shown="loadData"
    @hidden="resetForm"
    scrollable
    size="extra-xl"
    no-close-on-backdrop
    no-close-on-esc
    class="resume-invoice-modal"
  >
    <template #default="{}">
      <form @submit.prevent="submitFormResume" enctype="multipart/form-data" class="position-relative">
        <!-- Global loading overlay (Vuex-driven) -->
        <loading-overlay :show="globalLoading || loading || loadingResume || loadingProject" :message="saveSpinnerMessage" />
        <!-- Sección 10: Tipo de Actuación (Primero para determinar si es acción formativa) -->
        <ActionTypeSection
          :local-resume="localResume"
          :v="$v.localResume"
          @update:action_type="(value) => updateField('action_type', value)"
        />

        <!-- Sección 1: Relaciones y Configuración -->
        <RelationsConfigSection
          :local-resume="localResume"
          :v="$v.localResume"
          :select-options-subsidizable-actuations="selectOptionsSubsidizableActuations"
          :select-options-subsidizable-actions="selectOptionsSubsidizableActions"
          :select-options-codings="selectOptionsCodings"
          :select-options-phases="selectOptionsPhases"
          :select-options-primary-projects="selectOptionsPrimaryProjects"
          :select-options-expense-typologies="selectOptionsExpenseTypologies"
          :justification-type-options="justificationTypeOptions"
          @update:field="(field, value) => updateField(field, value)"
        />

        <!-- Sección 2: Externalizaciones/Contratos -->
        <ExternalizationsSection
          :local-resume="localResume"
          :v="$v.localResume"
          @update:field="(field, value) => updateField(field, value)"
        />

        <!-- Sección 3: Datos sobre Entidad Pagadora -->
        <PayerEntitySection
          :local-resume="localResume"
          :v="$v.localResume"
          :essay_id="essay_id"
          @update:field="(field, value) => updateField(field, value)"
        />

        <!-- Sección 4: Datos sobre Perceptor/a -->
        <PerceptorSection
          :local-resume="localResume"
          @update:field="(field, value) => updateField(field, value)"
        />

        <!-- Sección 5: Datos sobre el Gasto -->
        <ExpenseSection
          :local-resume="localResume"
          :is-formative-action="isFormativeAction"
          :v="$v.localResume"
          @update:field="(field, value) => updateField(field, value)"
        />

        <!-- Sección 6: Datos sobre el Pago -->
        <PaymentSection
          :local-resume="localResume"
          :is-formative-action="isFormativeAction"
          :essay="essay"
          :show-element="showElement"
          @update:field="(field, value) => updateField(field, value)"
          @file-change="onFileChangeResume"
        />

        <!-- Sección 7: Información Adicional -->
        <AdditionalInfoSection
          :local-resume="localResume"
          :is-formative-action="isFormativeAction"
          @update:field="(field, value) => updateField(field, value)"
        />

        <!-- Sección 8: Número de Orden -->
        <OrderNumberSection
          :local-resume="localResume"
          @update:field="(field, value) => updateField(field, value)"
        />

      </form>
    </template>
    <template #modal-footer="{}">
      <b-button
        v-if="!showElement([1, 3], essay?.essay_status_id == 7)"
        variant="success"
        @click="submitFormResume()"
        :disabled="loading"
      >
        <span
          class="spinner-border spinner-border-sm mr-2"
          aria-hidden="true"
          v-if="loading"
        ></span>
        Guardar
      </b-button>
      <b-button @click="cancelForm()">Cancelar</b-button>
    </template>
  </b-modal>
</template>
